/**
 * Tweaks — user-adjustable UI preferences persisted across sessions.
 *
 * Theme, accent, density, the topographic motif, and the active site
 * index. Persisted in localStorage; changes are applied to the document
 * root so CSS variables can react.
 *
 * IMPORTANT: this hook is backed by a React Context (`TweaksProvider`) so
 * that all consumers — App, ProjectScopedSpacetime, etc. — share a single
 * state. The old per-`useState` version diverged across components: App's
 * site-picker change wouldn't reach the inner SpacetimeDB provider, so
 * switching Sites silently failed to re-open `core` against the new
 * project. Anything that calls `useTweaks()` must be rendered inside a
 * `<TweaksProvider>`.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_UNIT_SYSTEM } from '@twin-builder/units';
import type { Tweaks } from '@/data/types';

const DEFAULTS: Tweaks = {
  theme: 'dark',
  accent: 'safety',
  density: 'comfortable',
  showTopo: true,
  showAiGuide: false,
  // -1 = no explicit choice yet. App.tsx's site resolution treats this
  // as a sentinel and prefers the `minezero-sandbox` site when present;
  // any explicit pick from the topbar picker stores a real index here
  // and persists across sessions. Existing users with a stored
  // siteIndex >= 0 keep whatever they last selected.
  siteIndex: -1,
  // Metric until the user picks; an older stored mz_tweaks without `units`
  // gets this too via the spread in read().
  units: DEFAULT_UNIT_SYSTEM,
  measureSnap: true,
};

const STORAGE_KEY = 'mz_tweaks';

function read(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

function apply(t: Tweaks): void {
  const root = document.documentElement;
  root.className = `theme-${t.theme}`;
  root.dataset.density = t.density;
  root.dataset.accent = t.accent;
  root.dataset.topo = t.showTopo ? 'on' : 'off';
}

type TweaksContextValue = {
  tweaks: Tweaks;
  update: (partial: Partial<Tweaks>) => void;
};

const TweaksContext = createContext<TweaksContextValue | undefined>(undefined);

export function TweaksProvider({ children }: { children: ReactNode }) {
  const [tweaks, setTweaks] = useState<Tweaks>(read);

  useEffect(() => {
    apply(tweaks);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
  }, [tweaks]);

  const update = useCallback((partial: Partial<Tweaks>) => {
    setTweaks(prev => ({ ...prev, ...partial }));
  }, []);

  const value = useMemo(() => ({ tweaks, update }), [tweaks, update]);

  return createElement(TweaksContext.Provider, { value }, children);
}

export function useTweaks(): TweaksContextValue {
  const ctx = useContext(TweaksContext);
  if (!ctx) {
    throw new Error(
      'useTweaks must be used inside <TweaksProvider>. Wrap the tree (typically in main.tsx) before rendering anything that reads tweaks.',
    );
  }
  return ctx;
}
